import React, { useState } from 'react';
import {
  X,
  Save,
  RotateCcw,
  Eye,
  Edit3,
  CheckCircle2,
  Send,
  Sparkles,
  Info,
  Copy,
} from 'lucide-react';
import { EmailTemplate, EmailSettings, VoterProfile } from '../types';
import {
  TEMPLATE_VARIABLES,
  getDefaultTemplates,
  getSampleVoter,
  renderEmailTemplate,
} from '../utils/emailTemplateEngine';

interface EmailTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: Record<string, EmailTemplate>;
  settings: EmailSettings;
  electionTitle: string;
  onSaveTemplates: (updated: Record<string, EmailTemplate>) => void;
  onSendTestEmail: (rendered: { subject: string; body: string; to: string }) => void;
}

export const EmailTemplateModal: React.FC<EmailTemplateModalProps> = ({
  isOpen,
  onClose,
  templates,
  settings,
  electionTitle,
  onSaveTemplates,
  onSendTestEmail,
}) => {
  const [activeTemplateId, setActiveTemplateId] = useState<string>('credentials');
  const [localTemplates, setLocalTemplates] = useState<Record<string, EmailTemplate>>(templates);
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [copiedTag, setCopiedTag] = useState<string | null>(null);
  const [testSent, setTestSent] = useState<boolean>(false);

  if (!isOpen) return null;

  const currentTemplate = localTemplates[activeTemplateId] || getDefaultTemplates(electionTitle)[activeTemplateId];
  const sampleVoter = getSampleVoter();
  const rendered = renderEmailTemplate(currentTemplate, sampleVoter, settings, electionTitle);

  const handleUpdateCurrent = (field: 'subject' | 'body', value: string) => {
    setLocalTemplates((prev) => ({
      ...prev,
      [activeTemplateId]: {
        ...currentTemplate,
        [field]: value,
        isCustomized: true,
        updatedAt: new Date().toISOString(),
      },
    }));
  };

  const handleInsertTag = (tag: string) => {
    handleUpdateCurrent('body', currentTemplate.body + ' ' + tag);
    setCopiedTag(tag);
    setTimeout(() => setCopiedTag(null), 1800);
  };

  const handleResetCurrent = () => {
    const defaults = getDefaultTemplates(electionTitle);
    if (defaults[activeTemplateId]) {
      setLocalTemplates((prev) => ({
        ...prev,
        [activeTemplateId]: defaults[activeTemplateId],
      }));
    }
  };

  const handleSaveAll = () => {
    onSaveTemplates(localTemplates);
    onClose();
  };

  const handleTriggerTest = () => {
    onSendTestEmail({
      subject: rendered.subject,
      body: rendered.body,
      to: settings.supportEmail || 'admin@electpoll.com',
    });
    setTestSent(true);
    setTimeout(() => setTestSent(false), 3000);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(6px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#171619',
          border: '1px solid #2D2C31',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '860px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
          overflow: 'hidden',
          color: '#FFFFFF',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #2D2C31',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                backgroundColor: 'rgba(255, 153, 0, 0.12)',
                border: '1px solid rgba(255, 153, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FF9900',
              }}
            >
              <Edit3 size={18} />
            </div>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>
                Email Template Editor
              </h3>
              <p style={{ fontSize: '12px', color: '#8E8E93', margin: '2px 0 0 0' }}>
                Personalize copy, insert dynamic voter fields, and preview live emails.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#8E8E93',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '8px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Template Selector Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #2D2C31',
            backgroundColor: '#121114',
            overflowX: 'auto',
            padding: '4px 16px 0 16px',
            gap: '8px',
          }}
        >
          {[
            { id: 'credentials', label: '1. Login Details (Pass)' },
            { id: 'live', label: '2. Election is Live' },
            { id: 'reminder', label: '3. Urgent Reminder' },
            { id: 'custom', label: '4. Custom Message' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTemplateId(tab.id)}
              style={{
                padding: '12px 14px',
                border: 'none',
                background: 'none',
                borderBottom: activeTemplateId === tab.id ? '2px solid #FF9900' : '2px solid transparent',
                color: activeTemplateId === tab.id ? '#FFFFFF' : '#8E8E93',
                fontSize: '13px',
                fontWeight: activeTemplateId === tab.id ? 700 : 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Main Body with Editor & Live Preview Toggle */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {/* View Toggle Bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                type="button"
                onClick={() => setPreviewMode(false)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: '1px solid #2D2C31',
                  backgroundColor: !previewMode ? '#FF9900' : '#1E1D21',
                  color: !previewMode ? '#171619' : '#8E8E93',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Edit3 size={13} /> Edit Template
              </button>

              <button
                type="button"
                onClick={() => setPreviewMode(true)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: '1px solid #2D2C31',
                  backgroundColor: previewMode ? '#FF9900' : '#1E1D21',
                  color: previewMode ? '#171619' : '#8E8E93',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Eye size={13} /> Live Voter Preview
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                type="button"
                onClick={handleResetCurrent}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  backgroundColor: '#1E1D21',
                  border: '1px solid #2D2C31',
                  color: '#8E8E93',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
                title="Reset this template to Electpoll default"
              >
                <RotateCcw size={13} /> Reset Default
              </button>

              <button
                type="button"
                onClick={handleTriggerTest}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(56, 189, 248, 0.12)',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  color: '#38BDF8',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                {testSent ? <CheckCircle2 size={13} color="#10B981" /> : <Send size={13} />}
                <span>{testSent ? 'Sent to Admin!' : 'Send Test Sample'}</span>
              </button>
            </div>
          </div>

          {!previewMode ? (
            /* ================= EDIT MODE ================= */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Dynamic Variables Palette */}
              <div
                style={{
                  backgroundColor: '#1C1B20',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '1px solid #2D2C31',
                }}
              >
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#8E8E93', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Sparkles size={13} color="#FF9900" /> Click to Insert Dynamic Variable Tag:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {TEMPLATE_VARIABLES.map((v) => (
                    <button
                      key={v.tag}
                      type="button"
                      onClick={() => handleInsertTag(v.tag)}
                      style={{
                        backgroundColor: '#252429',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        color: '#FF9900',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'background 0.15s ease',
                      }}
                      title={v.description}
                    >
                      <span>{v.tag}</span>
                      <Copy size={10} style={{ opacity: 0.6 }} />
                    </button>
                  ))}
                </div>
                {copiedTag && (
                  <div style={{ fontSize: '11px', color: '#10B981', marginTop: '6px', fontWeight: 600 }}>
                    ✓ Appended {copiedTag} into template
                  </div>
                )}
              </div>

              {/* Subject Line Input */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#A1A1AA', marginBottom: '6px', textTransform: 'uppercase' }}>
                  Email Subject Line
                </label>
                <input
                  type="text"
                  value={currentTemplate.subject}
                  onChange={(e) => handleUpdateCurrent('subject', e.target.value)}
                  placeholder="Subject line with {{first_name}} or {{election_title}}..."
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    backgroundColor: '#1E1D21',
                    border: '1px solid #2D2C31',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Message Body Textarea */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#A1A1AA', marginBottom: '6px', textTransform: 'uppercase' }}>
                  Email Body (Markdown / Plain Text)
                </label>
                <textarea
                  rows={13}
                  value={currentTemplate.body}
                  onChange={(e) => handleUpdateCurrent('body', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px',
                    borderRadius: '10px',
                    backgroundColor: '#1E1D21',
                    border: '1px solid #2D2C31',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    lineHeight: 1.6,
                    fontFamily: 'monospace',
                    outline: 'none',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                  }}
                />
              </div>
            </div>
          ) : (
            /* ================= LIVE PREVIEW MODE ================= */
            <div
              style={{
                backgroundColor: '#FFFFFF',
                color: '#111827',
                borderRadius: '14px',
                padding: '24px 28px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                border: '1px solid #E5E7EB',
              }}
            >
              {/* Email Client Header Chrome */}
              <div style={{ borderBottom: '1px solid #E5E7EB', paddingBottom: '14px', marginBottom: '18px' }}>
                <div style={{ fontSize: '11px', color: '#6B7280', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
                  Preview: Sample Recipient — {sampleVoter.name} ({sampleVoter.matric})
                </div>
                <h4 style={{ fontSize: '18px', fontWeight: 800, margin: '8px 0 6px 0', color: '#111827' }}>
                  {rendered.subject}
                </h4>
                <div style={{ fontSize: '12px', color: '#4B5563', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <span><strong>From:</strong> {settings.senderName} &lt;{settings.accounts[0]?.email || 'elections@electpoll.com'}&gt;</span>
                  <span><strong>Reply-To:</strong> {settings.replyTo}</span>
                  <span><strong>To:</strong> {sampleVoter.email}</span>
                </div>
              </div>

              {/* Rendered Email Body */}
              <div
                style={{
                  fontSize: '14px',
                  lineHeight: 1.7,
                  color: '#1F2937',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                }}
              >
                {rendered.body}
              </div>

              {/* Security Footer Notice */}
              <div
                style={{
                  marginTop: '28px',
                  paddingTop: '16px',
                  borderTop: '1px dashed #D1D5DB',
                  fontSize: '11px',
                  color: '#6B7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Info size={13} color="#FF9900" />
                This is a real-time preview showing how credentials and links resolve for individual accredited voters.
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #2D2C31',
            backgroundColor: '#141316',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: '12px', color: '#8E8E93' }}>
            Variables like <code style={{ color: '#FF9900' }}>{`{{first_name}}`}</code>, <code style={{ color: '#FF9900' }}>{`{{matric}}`}</code>, and <code style={{ color: '#FF9900' }}>{`{{password}}`}</code> replace dynamically for each recipient.
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '9px 16px',
                borderRadius: '8px',
                backgroundColor: '#1E1D21',
                border: '1px solid #2D2C31',
                color: '#A1A1AA',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveAll}
              style={{
                padding: '9px 18px',
                borderRadius: '8px',
                backgroundColor: '#FF9900',
                border: 'none',
                color: '#171619',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Save size={15} /> Save All Templates
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
